(ns bleene.dag.connections
  (:require
   [react :as react]
   [applied-science.js-interop :as jsi]
   [react-konva :refer [Arrow Line Group]]
   [re-frame.core :as re-frame]
   [bleene.utils :as utils]
   [bleene.layout.theme :refer [edge-type->color]]
   [bleene.dag.dag-context :as dag-context]
   [clojure.string]))


(defn add-center-point [[start-x start-y end-x end-y]]
  (let [len-x             (js/Math.abs (- end-x start-x))
        len-y             (js/Math.abs (- end-y start-y))
        coeff-x           0.3
        coeff-y           0.1
        offset-x-len      (max (* len-x coeff-x) 50)
        offset-y-len      (* len-y coeff-y)
        left-offset-y-len (max offset-y-len 50) ;extra curve for horizontally equal rects
        [right? left?] [(< start-x end-x) (>= start-x end-x)]
        [up? down?] [(> start-y end-y) (<= start-y end-y)]
        center-points     (cond
                            (and right? up?)
                            [(+ start-x offset-x-len) (- start-y offset-y-len)
                             (- end-x offset-x-len) (+ end-y offset-y-len)]

                            (and right? down?)
                            [(+ start-x offset-x-len) (+ start-y offset-y-len)
                             (- end-x offset-x-len) (- end-y offset-y-len)]

                            (and left? up?)
                            [(+ start-x offset-x-len) (- start-y left-offset-y-len)
                             (- end-x offset-x-len) (+ end-y left-offset-y-len)]

                            (and left? down?)
                            [(+ start-x offset-x-len) (+ start-y left-offset-y-len)
                             (- end-x offset-x-len) (- end-y left-offset-y-len)])]
    (concat [start-x start-y]
            center-points
            [end-x end-y])))


(defn bezier? [[start-x start-y end-x end-y]]
  (let [distance (Math/sqrt (+ (Math/pow (- end-x start-x) 2)
                               (Math/pow (- end-y start-y) 2)))]
    (< 200 distance)))


(defn select-edge [event dag-id id]
  (let [stage       (jsi/call-in event [:target :getStage])
        click-event (str "click." id)]
    (re-frame/dispatch [:dag/select-edge dag-id id])

    ;; Set click handler on Stage to track outside node clicks
    ;; then unselect node and remove event handler.
    ;; Global handler becomes a mess to figure out the event context
    (jsi/call stage :on click-event
      (fn [new-event]
        (let [target (jsi/get new-event :target)
              ;; we have to find a node itself
              ;; event target could be any nested element
              node   (when (= (jsi/call target :name) "edge")
                       target)]
          (when (or (nil? node)
                    (not= (jsi/call node :id) id))
            (re-frame/dispatch-sync [:dag/select-edge dag-id nil])
            (jsi/call stage :off click-event)))))))


(defn edge-on-hover [event]
  (let [target    (jsi/get event :target)
        stage     (jsi/call target :getStage)
        container (jsi/call stage :container)]
    (jsi/assoc-in! container [:style :cursor] "pointer")))


(defn edge-on-leave [event]
  (let [target    (jsi/get event :target)
        stage     (jsi/call target :getStage)
        container (jsi/call stage :container)]
    (jsi/assoc-in! container [:style :cursor] "default")))


(defn add-temp-edge [dag-id id position]
  (re-frame/dispatch [:dag/add-temp-edge dag-id id position]))


(defn reset-edge [dag-id]
  (re-frame/dispatch [:dag/reset-temp-edge dag-id]))


(defn update-temp-edge [dag-id position]
  (re-frame/dispatch [:dag/update-temp-edge dag-id position]))


(defn temp-edge []
  (let [{:keys [dag-id]} (utils/context dag-context/context)
        {:keys [temp-edge]} (utils/query {:temp-edge [:dag dag-id :temp-connection]})
        {:keys [start end type]} temp-edge
        points [(:x start) (:y start) (:x end) (:y end)]
        color  (edge-type->color type)]
    (when (some? temp-edge)
      [:> Arrow {:bezier      false
                 :points      points
                 :stroke      color
                 :fill        color
                 :strokeWidth 1}])))


(defn connection [{:keys [id points in-cycle? selected? dag-id type animated? connected? connected-to-selected-node?]}]
  (let [line-ref (react/useRef nil)
        curved?  (bezier? points)]

    (when animated?
      (react/useEffect
       (fn []
         (when-let [line-node (jsi/get line-ref :current)]
           (let [animation (fn animation-fn []
                             (when (and (jsi/call line-node :getLayer)
                                        (jsi/call line-node :dashOffset))
                               (jsi/call line-node :to #js {:dashOffset -20 :duration 1})
                               (js/setTimeout (fn []
                                                (jsi/call line-node :dashOffset 0)
                                                (animation-fn))
                                              1000)))]
             (animation)))
         (fn []))
       #js []))

    [:> Line {:id             id
              :ref            line-ref
              :name           "edge"
              :bezier         curved?
              :points         (if curved?
                                (add-center-point points)
                                points)

               :transformsEnabled "position"
               :perfectDrawEnabled false
              :fill "red"
              :stroke         (cond
                               in-cycle?  (edge-type->color :in-cycle)
                               selected?  (edge-type->color :selected)
                               connected-to-selected-node? (edge-type->color :connected-to-selected-node)
                               :otherwise (edge-type->color type))
              :strokeWidth    (if in-cycle? 13 10)
              :hitStrokeWidth 10
              :on-click       #(select-edge % dag-id id)
              :on-mouse-enter edge-on-hover
              :on-mouse-leave edge-on-leave}]))


(defn connections []
  (let [{:keys [dag-id]} (utils/context dag-context/context)
        {:keys [edges selected-edge selected-node connected-edges listen-events?]}
        (utils/query {:edges           [:dag dag-id {:edges [:connections]}]
                      :selected-edge   [:dag dag-id :selected-edge]
                      :selected-node   [:dag dag-id :selected-node]
                      :connected-edges [:dag dag-id :connected-edges]
                      :listen-events?  [:dag dag-id :listen-events?]})
        ;; Disable entirely for now
        animated? false]
    (into
     [:> Group {:listening false}]
     (for [{:keys [id start end type in-cycle?]} edges
           :let [points     [(:x start) (:y start) (:x end) (:y end)]
                 selected?  (= selected-edge id)
                 connected? (or (nil? selected-node)
                                (contains? connected-edges id))

                 connected-to-selected-node? (clojure.string/includes? id selected-node)]]
       [:f> connection {:id id
                        :points points
                        :in-cycle? in-cycle?
                        :selected? selected?
                        :animated? animated?
                        :dag-id dag-id
                        :type type
                        :connected-to-selected-node? connected-to-selected-node?}]))))
